import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TeacherPanel from '../panel/TeacherPanel';
import '../styles/TeacherDashboard.css';
import {
  FaLink,
  FaChalkboardTeacher,
  FaEnvelope,
  FaTasks,
} from 'react-icons/fa';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from "../../../api/api";

// Teacher icons
import dashboardIcon from '../icons/dashboard.png';
import notificationsIcon from '../icons/notifications.png';
import resultsIcon from '../icons/results.png';
import classIcon from '../icons/class.png';
import classworkIcon from '../icons/classwork.png';
import homeworkIcon from '../icons/homework.svg';
import assignmentIcon from '../icons/assignment.svg';
import attendanceIcon from '../icons/attendance.png';

// icon map
const iconMap = {
  'Teacher Dashboard': dashboardIcon,
  'Teacher Attendance': attendanceIcon,
  'Notifications': notificationsIcon,
  'Results': resultsIcon,
  'Update Class Work': classworkIcon,
  'Update Home Work': homeworkIcon,
  'Update Assignment': assignmentIcon,
  'Schedule Class': classIcon,
};

// optional: color map
const colorMap = {
  'Teacher Dashboard': '#2196f3',
  'Teacher Attendance': '#4caf50',
  'Notifications': '#ff9800',
  'Results': '#8e24aa',
  'Update Class Work': '#f44336',
  'Update Home Work': '#ef5350',
  'Update Assignment': '#03a9f4',
  'Schedule Class': '#ff5722',
};

const TeacherDashboard = () => {
  const { teacherId } = useParams();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [modules, setModules] = useState([]);
  const navigate = useNavigate();

const token = localStorage.getItem("token");
const schoolId = localStorage.getItem("schoolId");

useEffect(() => {
  if (!token || !schoolId) {
    console.warn("Missing token or schoolId");
    return;
  }

  const fetchModules = async () => {
    try {
      const res = await api.get(`/api/modules/${schoolId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const enabledModules = res.data.filter(
        (m) => m.role === "teacher" && m.enabled
      );

      setModules(enabledModules);
    } catch (err) {
      console.error("Error fetching teacher modules:", err);
    }
  };

  fetchModules();
}, [token, schoolId]);

  const handleNavigate = (path) => {
    navigate(path.replace(':id', teacherId));
  };

  // Dummy data
  const teachers = [
    { id: 1, name: 'Rahul Sharma', subject: 'Maths', email: 'rahul@school.com', phone: '9876543210' },
    { id: 2, name: 'Neha Mehta', subject: 'Physics', email: 'neha@school.com', phone: '9123456780' },
    { id: 3, name: 'Amit Verma', subject: 'English', email: 'amit@school.com', phone: '9988776655' },
  ];

  const todayClasses = [
    { className: 'XI-A', subject: 'Maths', time: '9:00 AM - 10:00 AM' },
    { className: 'X-B', subject: 'Physics', time: '10:15 AM - 11:15 AM' },
    { className: 'IX-C', subject: 'English', time: '11:30 AM - 12:30 PM' },
  ];

  const attendanceData = [
    { name: 'Present', value: 80 },
    { name: 'Absent', value: 20 },
  ];
  const COLORS = ['#4caf50', '#f44336'];

  return (
    <>
      {/* Sidebar */}
      <TeacherPanel onToggle={setIsSidebarCollapsed} />

      {/* Main content */}
      <div className={`teacher-main-content ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="teacher-topbar">
          <span className="date-time">TODAY IS {new Date().toLocaleString()}</span>
          <span className="school-name">Semigon Learning</span>
        </div>

        {/* Dynamic Quick Links */}
        <h3 className="teacher-dashboard-title">
          <FaLink style={{ marginRight: '8px' }} /> Quick Links
        </h3>
        <div className="teacher-ql-links">
          {modules.map((mod, index) => (
  <div key={`${mod.module_id || mod.name}-${index}`}
              className="teacher-ql-card"
              onClick={() => handleNavigate(mod.path)}
              style={{
                cursor: 'pointer',
                backgroundColor: colorMap[mod.name] || '#607d8b',
              }}
            >
              <div className="teacher-ql-left">
                <div className="teacher-ql-label">{mod.name}</div>
              </div>
              <div className="teacher-ql-icon-chip">
                <img
                  src={iconMap[mod.name] || dashboardIcon}
                  alt={mod.name}
                  style={{ width: '24px', height: '24px' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Teacher List Table */}
        <div className="teacher-table">
          <h3>Teacher Details</h3>
          <table>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Name</th>
                <th>Subject</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map((t, i) => (
                <tr key={t.id}>
                  <td>{i + 1}</td>
                  <td>{t.name}</td>
                  <td>{t.subject}</td>
                  <td>{t.email}</td>
                  <td>{t.phone}</td>
                  <td>
                    <button onClick={() => setSelectedTeacher(t)}>Show Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Teacher Detail Card Overlay */}
        {selectedTeacher && (
          <div className="teacher-detail-overlay" onClick={() => setSelectedTeacher(null)}>
            <div className="teacher-detail-card" onClick={(e) => e.stopPropagation()}>
              <h3>{selectedTeacher.name} - {selectedTeacher.subject}</h3>
              <p>Email: {selectedTeacher.email}</p>
              <p>Phone: {selectedTeacher.phone}</p>
              <p>Additional info can go here...</p>
              <button onClick={() => setSelectedTeacher(null)}>Close</button>
            </div>
          </div>
        )}

        {/* Today's Classes */}
        <div className="teacher-table">
          <h3>Today's Classes</h3>
          <table>
            <thead>
              <tr>
                <th>Class</th>
                <th>Subject</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {todayClasses.map((cls, i) => (
                <tr key={i}>
                  <td>{cls.className}</td>
                  <td>{cls.subject}</td>
                  <td>{cls.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Attendance Pie Chart */}
        <div className="teacher-attendance-chart">
          <h3><FaChalkboardTeacher /> Attendance Overview</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={attendanceData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
                label
              >
                {attendanceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Admin Messages */}
        <div className="teacher-table">
          <h3><FaEnvelope /> Admin Messages</h3>
          <table>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Message</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>Staff meeting at 3 PM in Conference Hall</td>
                <td>26 Aug 2025</td>
              </tr>
              <tr>
                <td>2</td>
                <td>Submit monthly attendance by tomorrow</td>
                <td>25 Aug 2025</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Parent Messages */}
        <div className="teacher-table">
          <h3><FaEnvelope /> Parent Messages</h3>
          <table>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Parent Name</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>Mr. Sharma</td>
                <td>Request for extra tutoring for Maths</td>
              </tr>
              <tr>
                <td>2</td>
                <td>Mrs. Mehta</td>
                <td>Query about Physics homework</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Assignments */}
        <div className="teacher-table">
          <h3><FaTasks /> Assignments by Class</h3>
          <table>
            <thead>
              <tr>
                <th>Class</th>
                <th>Student Name</th>
                <th>Assignment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>XI-A</td>
                <td>Aarav Sharma</td>
                <td>Maths Chapter 5</td>
                <td>Submitted</td>
              </tr>
              <tr>
                <td>X-B</td>
                <td>Priya Mehta</td>
                <td>Physics Lab Report</td>
                <td>Pending</td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </>
  );
};

export default TeacherDashboard;
